/**
 * DeviceFilterBar — atalhos de plantão, painel de filtros avançados e chips
 * de filtro ATIVO da aba Dispositivos (DEVICES-FILTER-SORT-1).
 *
 * Componente CONTROLADO: todo o estado de filtro vive na página (e no
 * localStorage); aqui só existe o aberto/fechado do painel. Os eixos, as
 * opções e os rótulos vêm do registro puro em lib/deviceQuery.js — nada é
 * hardcoded aqui, então um eixo novo aparece sozinho no painel, no contador
 * e no "Limpar filtros".
 */
import { useMemo, useState } from "react";
import {
  ALL,
  FILTER_GROUPS,
  INSTALLED_PERIODS,
  activeFilterCount,
  activePresetId,
  applyPreset,
  clearAxis,
  clearFilters,
  describeActiveFilters,
  visibleAxes,
  visiblePresets,
} from "../../lib/deviceQuery.js";
import NavIcon from "../../nav/NavIcon.jsx";

/** Chip de atalho/período — mesma linguagem visual dos chips de status. */
function Chip({ active, onClick, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={
        "chip-filter shrink-0 "
        + (active
          ? "bg-noc-accent border-noc-accent text-noc-bg font-bold"
          : "text-noc-text2 border-noc-border hover:bg-noc-panel2")
      }
    >
      {children}
    </button>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="text-[10px] text-noc-muted block mb-0.5">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-xs bg-noc-bg/60 border border-noc-border
                   rounded px-2 py-1.5 text-noc-text h-9"
      >
        <option value={ALL}>— todos —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
            {o.count != null ? ` (${o.count})` : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

function FilterToggle({ label, checked, count, onChange }) {
  return (
    <label className="flex items-center gap-2 text-xs text-noc-muted
                      cursor-pointer select-none py-1.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-[#2BD9F0]"
      />
      <span className="min-w-0">
        {label}
        {count != null ? <span className="text-noc-faint"> ({count})</span> : null}
      </span>
    </label>
  );
}

/** Multi-seleção de versões de firmware — opções derivadas da própria frota. */
function FwMultiSelect({ label, value, options, onChange }) {
  if (!options.length) return null;
  const toggle = (v) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div role="group" aria-label={label}>
      <span className="text-[10px] text-noc-muted block mb-0.5" aria-hidden="true">
        {label}
        {value.length ? <span className="text-noc-accent"> · {value.length}</span> : null}
      </span>
      <div className="max-h-28 overflow-y-auto rounded border border-noc-border
                      bg-noc-bg/60 px-2 py-1 space-y-0.5">
        {options.map((o) => (
          <label
            key={o.value}
            className="flex items-center gap-2 text-[11px] text-noc-text2
                       cursor-pointer select-none"
          >
            <input
              type="checkbox"
              checked={value.includes(o.value)}
              onChange={() => toggle(o.value)}
              className="accent-[#2BD9F0]"
            />
            <span className="font-mono truncate" title={o.value}>{o.value}</span>
            <span className="ml-auto text-noc-faint">{o.count}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/** Período de instalação: pills de atalho + intervalo personalizado. */
function InstallPeriod({ view, onPatch }) {
  const custom = view.installedPeriod === "custom";
  return (
    <div className="sm:col-span-2 xl:col-span-1" role="group" aria-label="Data de instalação">
      <span className="text-[10px] text-noc-muted block mb-1" aria-hidden="true">
        Data de instalação
      </span>
      <div className="flex flex-wrap gap-1.5">
        {INSTALLED_PERIODS.map((p) => (
          <Chip
            key={p.value}
            active={view.installedPeriod === p.value}
            onClick={() =>
              onPatch(
                p.value === "custom"
                  ? { installedPeriod: "custom" }
                  : { installedPeriod: p.value, installedFrom: "", installedTo: "" },
              )
            }
          >
            {p.label}
          </Chip>
        ))}
      </div>
      {custom && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="block">
            <span className="text-[10px] text-noc-muted block mb-0.5">De</span>
            <input
              type="date"
              value={view.installedFrom}
              max={view.installedTo || undefined}
              onChange={(e) => onPatch({ installedFrom: e.target.value })}
              className="w-full text-xs bg-noc-bg/60 border border-noc-border
                         rounded px-2 py-1.5 text-noc-text h-9"
            />
          </label>
          <label className="block">
            <span className="text-[10px] text-noc-muted block mb-0.5">Até</span>
            <input
              type="date"
              value={view.installedTo}
              min={view.installedFrom || undefined}
              onChange={(e) => onPatch({ installedTo: e.target.value })}
              className="w-full text-xs bg-noc-bg/60 border border-noc-border
                         rounded px-2 py-1.5 text-noc-text h-9"
            />
          </label>
        </div>
      )}
    </div>
  );
}

export default function DeviceFilterBar({
  source = [],
  ctx = null,
  options = {},
  extras = {},
  view,
  onChange,
}) {
  const activeCount = activeFilterCount(view);
  const [open, setOpen] = useState(() => activeCount > 0);
  const patch = (p) => onChange({ ...view, ...p });

  const presets = useMemo(
    () => visiblePresets(source, ctx, extras),
    [source, ctx, extras],
  );
  const axes = useMemo(
    () => visibleAxes(source, ctx, extras),
    [source, ctx, extras],
  );
  const preset = activePresetId(view);
  const chips = describeActiveFilters(view, options, ctx);

  const renderAxis = (axis) => {
    const opt = options[axis.key] || {};
    if (axis.control === "toggle") {
      // marcador que não casa com ninguém é ruído — some, a menos que esteja
      // LIGADO (aí precisa continuar visível para poder ser desligado).
      if (!opt.count && !view[axis.key]) return null;
      return (
        <FilterToggle
          key={axis.key}
          label={axis.label}
          count={opt.count}
          checked={Boolean(view[axis.key])}
          onChange={(v) => patch({ [axis.key]: v })}
        />
      );
    }
    if (axis.control === "multi") {
      return (
        <FwMultiSelect
          key={axis.key}
          label={axis.label}
          value={view[axis.key] || []}
          options={opt.options || []}
          onChange={(v) => patch({ [axis.key]: v })}
        />
      );
    }
    let list = opt.options || [];
    // A opção ATIVA nunca pode sumir da lista: se ela zerou a contagem (o
    // device voltou a bater, o relé religou…) o <select> ficaria em branco
    // com o filtro AINDA aplicado, e o operador não teria como desligá-lo.
    const active = view[axis.key];
    if (active !== ALL && !list.some((o) => o.value === active)) {
      const known = (axis.options || []).find((o) => o.value === active);
      list = [{ value: active, label: known ? known.label : active, count: 0 }, ...list];
    }
    // um eixo com uma opção só não segmenta nada — não ocupa espaço na tela
    if (list.length < 2 && active === ALL) return null;
    return (
      <FilterSelect
        key={axis.key}
        label={axis.label}
        value={active}
        options={list}
        onChange={(v) => patch({ [axis.key]: v })}
      />
    );
  };

  return (
    <div className="space-y-3">
      {/* ---- atalhos de plantão ---- */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.16em] font-mono
                        text-noc-faint mb-1.5">
          Atalhos de plantão
        </div>
        <div
          role="group"
          aria-label="Atalhos de plantão"
          className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
        >
          {presets.map((p) => (
            <Chip
              key={p.id}
              active={preset === p.id}
              onClick={() =>
                onChange(preset === p.id ? clearFilters(view) : applyPreset(view, p.id))
              }
              title={
                p.count === 0
                  ? "Nenhum dispositivo nesta condição agora"
                  : `${p.count} dispositivo(s) nesta condição`
              }
            >
              {p.label} <span className="opacity-70">{p.count}</span>
            </Chip>
          ))}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className={
              "chip-filter shrink-0 ml-auto "
              + (activeCount > 0
                ? "text-noc-accent border-noc-accent/40 hover:bg-noc-accent/10"
                : "text-noc-text2 border-noc-border hover:bg-noc-panel2")
            }
          >
            <NavIcon name="filter" size={13} />
            Filtros avançados
            {activeCount > 0 ? <span className="opacity-80">{activeCount}</span> : null}
            <span aria-hidden="true">{open ? "▾" : "▸"}</span>
          </button>
        </div>
      </div>

      {/* ---- chips de filtro ATIVO (visíveis mesmo com o painel fechado) ---- */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.14em] font-mono
                           text-noc-faint">
            Filtros ativos
          </span>
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => onChange(clearAxis(view, c.key))}
              title="Remover este filtro"
              className="inline-flex items-center gap-1 text-[10.5px] px-2 py-1
                         rounded-md border border-noc-accent/30 text-noc-accent
                         bg-noc-accent/10 hover:bg-noc-accent/20 transition-colors"
            >
              {c.label}
              <span aria-hidden="true" className="opacity-70">×</span>
            </button>
          ))}
        </div>
      )}

      {/* ---- painel avançado ---- */}
      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-4 gap-y-3
                        pt-3 border-t border-noc-divider">
          {FILTER_GROUPS.map((g) => {
            const groupAxes = axes.filter((a) => a.group === g.id);
            const rendered = groupAxes.map(renderAxis).filter(Boolean);
            const isInstall = g.id === "install";
            if (!rendered.length && !isInstall) return null;
            return (
              <div key={g.id} className="space-y-2 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.16em] font-mono
                                text-noc-faint">
                  {g.label}
                </div>
                {isInstall && <InstallPeriod view={view} onPatch={patch} />}
                {rendered}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
